/**
 * Bootstrap de sessão: `POST /v1/u1/sessions`.
 *
 * O front não fala com o stream sem antes ter `session_id` e `ticket`. O
 * ticket é uma credencial efêmera de uso único para abrir o WebSocket — o
 * token de identidade não serve sozinho porque o handshake de WS não permite
 * cabeçalho `Authorization`, e passar o JWT longo na query o deixaria em log
 * de acesso do nginx.
 *
 * ## Por que `authFetch` e não `fetch`
 *
 * Desde a v0.2 o servidor resolve o tenant a partir do token e recusa quem não
 * apresenta um. `authFetch` (ver `auth/authClient.ts`) é quem anexa o
 * `Authorization` e, num 401 de identidade, renova e repete uma vez.
 *
 * Repare no que **não** mudou: o corpo continua sem `tenant_id`. O `tenant_id`
 * que volta em `SessionBootstrap` é leitura informativa — o cliente nunca
 * escolhe onde a sessão nasce, e mandá-lo faria o servidor responder 400.
 */

import { DEFAULT_VOICE_MODE, type VoiceMode } from '@will/contracts';
import { authFetch } from '../auth';
import { U1_SESSIONS_URL } from './config';

export interface SessionBootstrap {
  session_id: string;
  ticket: string;
  tenant_id: string;
  /** ISO-8601; depois disso o ticket não abre mais o stream */
  expires_at: string;
  /** modo efetivo — o servidor pode recusar o pedido do cliente (ADR-007) */
  voice_mode: VoiceMode;
}

export interface SessionRequest {
  voice_mode?: VoiceMode;
  language?: string;
  /** retomada explícita de uma sessão anterior, quando o cliente recarregou */
  resume_session_id?: string;
}

export class SessionError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message);
    this.name = 'SessionError';
  }
}

const CHAVE_ARMAZENAMENTO = 'will.u1.sessao';
const TENTATIVAS = 3;

let sessaoAtual: SessionBootstrap | null = null;

/**
 * Uma única requisição em voo, compartilhada.
 *
 * O StrictMode do React monta, desmonta e remonta o App em desenvolvimento;
 * sem esta memoização o boot criaria duas sessões e a segunda invalidaria o
 * ticket da primeira.
 */
let promessaEmVoo: Promise<SessionBootstrap> | null = null;

/** Cria (ou reaproveita) a sessão do U1. Idempotente dentro da mesma carga da página. */
export function createSession(pedido: SessionRequest = {}): Promise<SessionBootstrap> {
  if (sessaoAtual && !expirou(sessaoAtual)) return Promise.resolve(sessaoAtual);
  if (promessaEmVoo) return promessaEmVoo;

  const armazenada = loadStoredSession();
  const corpo: SessionRequest = {
    voice_mode: pedido.voice_mode ?? DEFAULT_VOICE_MODE,
    language: pedido.language ?? 'pt-BR',
    // Se havia sessão viva antes do reload, pedimos a mesma de volta: o
    // Presence tem uma janela de graça (U1_RECONNECT_GRACE_MS) e o histórico
    // do turno em curso é retomado por `since=`.
    ...(pedido.resume_session_id ?? armazenada?.session_id
      ? { resume_session_id: pedido.resume_session_id ?? armazenada?.session_id }
      : {}),
  };

  promessaEmVoo = solicitar(corpo)
    .then((sessao) => {
      sessaoAtual = sessao;
      guardar(sessao);
      return sessao;
    })
    .finally(() => {
      promessaEmVoo = null;
    });

  return promessaEmVoo;
}

/**
 * Compra um ticket novo para a sessão que já existe.
 *
 * Existe por causa de um laço visto em produção. O ticket vive dois minutos e é
 * renovado PELO PRÓPRIO STREAM; se o stream cair depois do vencimento, a
 * renovação nunca chega. O navegador então reapresenta o ticket morto a cada
 * tentativa, o servidor recusa o handshake com 401, e o navegador só enxerga
 * `close 1006` — sem motivo, sem saída. Reconectando para sempre.
 *
 * O token de acesso dura muito mais que o ticket, e é ele que compra o novo.
 * Quando nem a sessão existe mais (404), aí sim vale criar outra: devolvemos a
 * nova, e quem chamou decide o que fazer com a descontinuidade.
 */
export async function renovarTicket(
  sessionId: string,
): Promise<{ ticket: string; sessionId: string; nova: boolean }> {
  const resposta = await authFetch(`${U1_SESSIONS_URL}/${encodeURIComponent(sessionId)}/ticket`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (resposta.status === 404) {
    // A sessão morreu de vez. Insistir nela é o próprio laço.
    clearSession();
    const nova = await createSession();
    return { ticket: nova.ticket, sessionId: nova.session_id, nova: true };
  }

  if (!resposta.ok) {
    throw new SessionError(
      `renovação de ticket respondeu ${resposta.status}`,
      resposta.status,
    );
  }

  const dado: unknown = await resposta.json();
  const ticket =
    typeof dado === 'object' && dado !== null
      ? (dado as Record<string, unknown>)['ticket']
      : undefined;
  if (typeof ticket !== 'string' || ticket === '') {
    throw new SessionError('renovação de ticket sem ticket no corpo', resposta.status);
  }

  // A sessão em memória também guarda o ticket: sem isto, um reload logo depois
  // da renovação voltaria a usar o vencido.
  if (sessaoAtual && sessaoAtual.session_id === sessionId) {
    sessaoAtual = { ...sessaoAtual, ticket };
    guardar(sessaoAtual);
  }
  return { ticket, sessionId, nova: false };
}

export function getSession(): SessionBootstrap | null {
  return sessaoAtual;
}

export function clearSession(): void {
  sessaoAtual = null;
  promessaEmVoo = null;
  try {
    window.sessionStorage.removeItem(CHAVE_ARMAZENAMENTO);
  } catch {
    // sessionStorage pode estar bloqueado (modo privado, iframe sem permissão).
    // Perder a retomada é aceitável; quebrar o boot por causa disso, não.
  }
}

/** Recupera a sessão guardada no `sessionStorage`, se ainda não expirou. */
export function loadStoredSession(): SessionBootstrap | null {
  try {
    const cru = window.sessionStorage.getItem(CHAVE_ARMAZENAMENTO);
    if (!cru) return null;
    const dado: unknown = JSON.parse(cru);
    if (!ehSessao(dado) || expirou(dado)) {
      window.sessionStorage.removeItem(CHAVE_ARMAZENAMENTO);
      return null;
    }
    return dado;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------

async function solicitar(corpo: SessionRequest): Promise<SessionBootstrap> {
  let ultimoErro: SessionError = new SessionError('sessão não pôde ser criada', null);

  for (let tentativa = 0; tentativa < TENTATIVAS; tentativa += 1) {
    try {
      const resposta = await authFetch(U1_SESSIONS_URL, {
        method: 'POST',
        // Mesma origem (ADR-008): o cookie de renovação viaja sem configuração
        // extra, e o `Authorization` é anexado pelo próprio authFetch.
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(corpo),
      });

      if (!resposta.ok) {
        const erro = new SessionError(
          `POST ${U1_SESSIONS_URL} respondeu ${resposta.status}`,
          resposta.status,
        );
        // 4xx é contrato quebrado ou falta de credencial: repetir não conserta.
        if (resposta.status < 500) throw erro;
        ultimoErro = erro;
      } else {
        const dado: unknown = await resposta.json();
        if (!ehSessao(dado)) {
          throw new SessionError(
            'resposta de /sessions sem session_id ou ticket — contrato divergente',
            resposta.status,
          );
        }
        return dado;
      }
    } catch (causa) {
      if (causa instanceof SessionError && causa.status !== null && causa.status < 500) throw causa;
      ultimoErro =
        causa instanceof SessionError
          ? causa
          : new SessionError(`falha de rede ao criar sessão: ${String(causa)}`, null);
    }

    if (tentativa < TENTATIVAS - 1) await esperar(400 * 2 ** tentativa);
  }

  throw ultimoErro;
}

function guardar(sessao: SessionBootstrap): void {
  try {
    window.sessionStorage.setItem(CHAVE_ARMAZENAMENTO, JSON.stringify(sessao));
  } catch {
    // ver clearSession(): armazenamento indisponível não é erro fatal.
  }
}

function expirou(sessao: SessionBootstrap): boolean {
  const limite = Date.parse(sessao.expires_at);
  if (Number.isNaN(limite)) return false; // sem validade legível, assumimos viva
  return limite <= Date.now();
}

/**
 * Só `session_id` e `ticket` são exigidos: são os dois campos sem os quais o
 * stream não abre. Os demais têm degradação definida (validade ilegível conta
 * como viva, modo de voz cai no padrão), então recusar a resposta inteira por
 * causa deles trocaria uma sessão utilizável por nenhuma.
 */
function ehSessao(v: unknown): v is SessionBootstrap {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o['session_id'] === 'string' && o['session_id'] !== ''
    && typeof o['ticket'] === 'string' && o['ticket'] !== '';
}

function esperar(ms: number): Promise<void> {
  return new Promise((resolver) => {
    window.setTimeout(resolver, ms);
  });
}
